<template>
  <div>
    <v-skeleton-loader v-if="isLoading" type="table" />
    <v-alert v-else-if="error" type="error" dense>{{ error }}</v-alert>
    <template v-else-if="categoryAndVariants.length || promotions.length">
      <v-tabs v-model="selectedTab" background-color="primary" dark show-arrows>
        <v-tab
          v-for="{ category } in categoryAndVariants"
          :key="category.id"
          :href="`#${category.code}`">
          {{ category.name }}
        </v-tab>
        <v-tab v-if="promotions.length" href="#PROMO">
          {{ $t('entities.promotions.title') }}
        </v-tab>
      </v-tabs>
      <v-card class="elevation-2">
        <v-tabs-items v-model="selectedTab">
          <v-tab-item
            v-for="{ category, variants } in categoryAndVariants"
            :key="category.id"
            :value="category.code">
            <v-simple-table>
              <thead>
                <tr>
                  <th style="width: 40px"></th>
                  <th style="width: 80px">
                    {{ $t('generalAttributes.sortOrder') }}
                  </th>
                  <th style="width: 64px">
                    {{ $t('generalAttributes.image') }}
                  </th>
                  <th>{{ $t('generalAttributes.name') }}</th>
                  <th>{{ $t('generalAttributes.price') }}</th>
                </tr>
              </thead>
              <draggable
                tag="tbody"
                :list="variants"
                handle=".drag-handle"
                @end="onVariantDragEnd(variants)">
                <tr v-for="(variant, index) in variants" :key="variant.id">
                  <td>
                    <v-icon class="drag-handle" style="cursor: grab"
                      >mdi-drag
                    </v-icon>
                  </td>
                  <td class="grey--text">{{ index + 1 }}</td>
                  <td>
                    <v-img
                      :src="variant.image"
                      width="40"
                      height="40"
                      contain
                      class="my-1" />
                  </td>
                  <td>{{ variant.name }}</td>
                  <td>{{ formatVariantPrice(variant) }}</td>
                </tr>
              </draggable>
            </v-simple-table>
            <v-progress-linear
              v-if="savingVariants"
              indeterminate
              color="primary" />
          </v-tab-item>

          <v-tab-item v-if="promotions.length" value="PROMO">
            <v-simple-table>
              <thead>
                <tr>
                  <th style="width: 40px"></th>
                  <th style="width: 80px">
                    {{ $t('generalAttributes.sortOrder') }}
                  </th>
                  <th style="width: 64px">
                    {{ $t('generalAttributes.image') }}
                  </th>
                  <th>{{ $t('generalAttributes.name') }}</th>
                  <th>{{ $t('entities.promotions.card.discount') }}</th>
                </tr>
              </thead>
              <draggable
                tag="tbody"
                :list="promotions"
                handle=".drag-handle"
                @end="onPromotionDragEnd">
                <tr
                  v-for="(promotion, index) in promotions"
                  :key="promotion.id">
                  <td>
                    <v-icon class="drag-handle" style="cursor: grab"
                      >mdi-drag
                    </v-icon>
                  </td>
                  <td class="grey--text">{{ index + 1 }}</td>
                  <td>
                    <v-img
                      :src="promotion.image"
                      width="40"
                      height="40"
                      contain
                      class="my-1" />
                  </td>
                  <td>{{ promotion.name }}</td>
                  <td>{{ formatDiscount(promotion) }}</td>
                </tr>
              </draggable>
            </v-simple-table>
            <v-progress-linear
              v-if="savingPromotions"
              indeterminate
              color="primary" />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </template>
    <v-alert v-else type="info" dense>
      {{ $t('components.noData.noEntriesFound') }}
    </v-alert>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import draggable from 'vuedraggable'
import ArenaProduct from '@/models/shop/ArenaProduct'
import Promotion from '@/models/shop/Promotion'
import Category from '@/models/shop/CategoryV2'
import Currency from '@/models/shop/Currency'
import { getVariantPrice, getVariantCurrency } from '@/utils/variant'

export default {
  name: 'SortOrder',
  components: { draggable },
  data: () => ({
    isLoading: false,
    savingVariants: false,
    savingPromotions: false,
    error: '',
    categoryAndVariants: [],
    promotions: [],
    selectedTab: null,
    arenaCurrency: null,
  }),
  computed: {
    ...mapGetters(['arena']),
    arenaId() {
      return this.arena?.id
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    formatVariantPrice(variant) {
      const price = getVariantPrice(variant)
      const currency = getVariantCurrency(variant)
      if (!price) return '—'
      return currency ? `${price} ${currency}` : price
    },
    formatDiscount(promotion) {
      if (promotion.discount_type === 'percentage') {
        return `${promotion.discount_value}%`
      }
      return `${this.$t('entities.promotions.card.discountFixedPrice')}: ${
        promotion.discount_value
      }`
    },
    async fetchCurrency() {
      try {
        const res = await Currency.api.getArenaCurrencies(this.arenaId)
        this.arenaCurrency = res.data?.data?.[0] ?? null
      } catch {
        this.arenaCurrency = null
      }
    },
    async fetchVariants() {
      if (!this.arenaCurrency?.id) return
      const res = await ArenaProduct.api.getArenaVariants(this.arenaId, {
        currency_id: this.arenaCurrency.id,
      })
      const data = res.data?.data ?? []
      this.categoryAndVariants = data.map(({ category, variants }) => ({
        category: new Category(category),
        variants,
      }))
    },
    async fetchPromotions() {
      const res = await Promotion.api.list(this.arenaId)
      this.promotions = res.data?.data ?? []
    },
    async fetchAll() {
      if (!this.arenaId) return
      this.isLoading = true
      this.error = ''
      try {
        await this.fetchCurrency()
        await Promise.all([this.fetchVariants(), this.fetchPromotions()])
        if (this.categoryAndVariants.length) {
          this.selectedTab = this.categoryAndVariants[0].category.code
        } else if (this.promotions.length) {
          this.selectedTab = 'PROMO'
        }
      } catch (err) {
        this.error = err?.response?.data?.message || this.$t('errors.default')
      } finally {
        this.isLoading = false
      }
    },
    async onVariantDragEnd(variants) {
      this.savingVariants = true
      try {
        const changed = variants.filter(
          (v, index) => v.sort_order !== index + 1,
        )
        await Promise.all(
          changed.map((variant) =>
            ArenaProduct.api.updateArenaVariant(
              this.arenaId,
              variant.arena_product_id,
              variant.id,
              { sort_order: variants.indexOf(variant) + 1 },
            ),
          ),
        )
        variants.forEach((v, index) => {
          v.sort_order = index + 1
        })
        this.showSnackbar({
          message: this.$t('entities.products.notifications.update'),
          color: 'success',
        })
      } catch {
        this.showSnackbar({
          message: this.$t('errors.default'),
          color: 'error',
        })
      } finally {
        this.savingVariants = false
      }
    },
    async onPromotionDragEnd() {
      this.savingPromotions = true
      try {
        const changed = this.promotions.filter(
          (p, index) => p.sort_order !== index + 1,
        )
        await Promise.all(
          changed.map((promotion) =>
            Promotion.api.update(this.arenaId, promotion.id, {
              name: promotion.name,
              discount_value: promotion.discount_value,
              discount_type: promotion.discount_type,
              sort_order: this.promotions.indexOf(promotion) + 1,
            }),
          ),
        )
        this.promotions.forEach((p, index) => {
          p.sort_order = index + 1
        })
        this.showSnackbar({
          message: this.$t('entities.products.notifications.update'),
          color: 'success',
        })
      } catch {
        this.showSnackbar({
          message: this.$t('errors.default'),
          color: 'error',
        })
      } finally {
        this.savingPromotions = false
      }
    },
  },
  async created() {
    await this.fetchAll()
  },
}
</script>
